import {
  buttonTokens,
  linkTokens,
  mainTokens,
  primaryTokens,
  ThemeMode,
  TokenMapping,
} from './tokens';
import {
  argbFromHex,
  hctFromInt,
  hexFromArgb,
  tonalPaletteFromInt,
} from './hct';

export const DEFAULT_MAIN_COLOR = '#19214D';
export const DEFAULT_BUTTON_COLOR = '#007AFF';
export const DEFAULT_LINK_COLOR = '#007AFF';

export interface OrganisationThemeColors {
  main_color?: string | null;
  button_color?: string | null;
  link_color?: string | null;
  dark_main_color?: string | null;
  dark_button_color?: string | null;
  dark_link_color?: string | null;
}

export type TonalScale = Record<number, string>;
export type ResolvedBrandTokens = Record<string, string>;

function getValidHex(value: string | null | undefined, fallback: string): string {
  if (!value) return fallback;

  try {
    argbFromHex(value);
    return value;
  } catch {
    return fallback;
  }
}

export function generatePalette(hex: string): TonalScale {
  const palette = tonalPaletteFromInt(argbFromHex(hex));
  const scale: TonalScale = {};

  for (let tone = 0; tone <= 100; tone += 1) {
    scale[tone] = hexFromArgb(palette.tone(tone));
  }

  return scale;
}

export function getSeedTone(hex: string): number {
  return hctFromInt(argbFromHex(hex)).tone;
}

function resolveTokenMap(
  seedHex: string,
  tokenMap: Record<string, TokenMapping>,
  mode: ThemeMode,
  primaryTokenNames: readonly string[],
  overrideHex?: string,
): ResolvedBrandTokens {
  const palette = generatePalette(seedHex);
  const seedTone = getSeedTone(seedHex);
  const literalTokens = new Set(primaryTokenNames);
  const resolved: ResolvedBrandTokens = {};

  Object.entries(tokenMap).forEach(([token, mapping]) => {
    if (overrideHex && literalTokens.has(token)) {
      resolved[token] = overrideHex;
      return;
    }

    const resolver = mapping[mode];
    const tone = typeof resolver === 'function' ? resolver(seedTone) : resolver;
    resolved[token] = palette[tone];
  });

  return resolved;
}

function relativeLuminance(hex: string): number {
  const normalized = hex.replace('#', '');
  const channels = [0, 2, 4].map((offset) => parseInt(normalized.slice(offset, offset + 2), 16) / 255);
  const [red, green, blue] = channels.map(
    (channel) => (channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4),
  );

  return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
}

function contrastRatio(first: string, second: string): number {
  const firstLuminance = relativeLuminance(first);
  const secondLuminance = relativeLuminance(second);
  const lighter = Math.max(firstLuminance, secondLuminance);
  const darker = Math.min(firstLuminance, secondLuminance);

  return (lighter + 0.05) / (darker + 0.05);
}

export function getOnColor(background: string): string {
  const light = '#FFFFFF';
  const dark = '#18181B';
  return contrastRatio(background, light) >= contrastRatio(background, dark) ? light : dark;
}

export function resolveBrandTokens(
  theme: OrganisationThemeColors = {},
  mode: ThemeMode = 'light',
): ResolvedBrandTokens {
  const mainHex = getValidHex(theme.main_color, DEFAULT_MAIN_COLOR);
  const buttonHex = getValidHex(theme.button_color, DEFAULT_BUTTON_COLOR);
  const linkHex = getValidHex(theme.link_color, DEFAULT_LINK_COLOR);
  const isDark = mode === 'dark';

  const mainOverride = isDark && theme.dark_main_color
    ? getValidHex(theme.dark_main_color, mainHex)
    : undefined;
  const buttonOverride = isDark && theme.dark_button_color
    ? getValidHex(theme.dark_button_color, buttonHex)
    : undefined;
  const linkOverride = isDark && theme.dark_link_color
    ? getValidHex(theme.dark_link_color, linkHex)
    : undefined;

  const resolved = {
    ...resolveTokenMap(
      mainOverride || mainHex,
      mainTokens,
      mode,
      primaryTokens.main,
      mainOverride,
    ),
    ...resolveTokenMap(
      buttonOverride || buttonHex,
      buttonTokens,
      mode,
      primaryTokens.button,
      buttonOverride,
    ),
    ...resolveTokenMap(
      linkOverride || linkHex,
      linkTokens,
      mode,
      primaryTokens.link,
      linkOverride,
    ),
  };

  resolved['--text-on-brand'] = getOnColor(resolved['--bg-brand']);
  resolved['--text-on-button'] = getOnColor(resolved['--bg-button']);
  resolved['--text-on-top-bar'] = getOnColor(resolved['--bg-top-bar']);

  return resolved;
}
